import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import type { RootState } from "../../common/state/store";
import {
  getEditedProjectGroup,
  getNewProjectGroup,
  getSelectedProjectGroup,
  setEditedMenuMedia,
  setNewProjectGroup,
  updateProjectGroup,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { DynamicMedia } from "../../features/graph/components/DynamicField";
import { setMediaSearch, setOriginalMediaGallery } from "../../features/gallery/state/gallerySlice";
import { useMediaById } from "../../features/gallery/useMediaById";
import { useEffect, useState } from "react";
import type { IMedia } from "../../model/unityObject";
import { closeStandardModal, openStandardModal } from "../../common/state/slice/modal/modalSlice";
import ModalGallery from "../../features/graph/components/ModalGallery";
import { FooterButtons } from "../../common/components/FooterButtons";
import { PROJECT_GROUP_LIST_COMPONENT_ID } from "src/features/projectGroups/components/ProjectGroupList";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { useTourView } from "src/features/onboarding/engine/useTourView";
import { TOUR_VIEW_ANCHORS } from "src/features/onboarding/engine/tourViewAnchors";

interface ICreateProjectGroupMenuMedia {
  incrementStep?: () => void;
  decrementStep?: () => void;
  editMode?: boolean;
}

const MAX_MEDIA_SIZE_IN_BYTE = 30 * 1024 * 1024;
const DEFAULT_MENU_MEDIA_ID = 5346;

const CreateProjectGroupMenuMedia = ({
  incrementStep,
  decrementStep,
  editMode = false,
}: ICreateProjectGroupMenuMedia) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  useTourView("course-wizard-menu-media", true, TOUR_VIEW_ANCHORS["course-wizard-menu-media"]);

  const { register, handleSubmit, setValue, control } = useForm();

  const { newProjectGroup, selectedProjectGroup, editedProjectGroup } = useAppSelector(
    (state: RootState) => {
      return {
        newProjectGroup: getNewProjectGroup(state),
        selectedProjectGroup: getSelectedProjectGroup(state),
        editedProjectGroup: getEditedProjectGroup(state),
      };
    },
  );

  const initialMenuMediaId = (): number => {
    if (editMode) {
      return Number(editedProjectGroup?.source_app_main_menu_media?.id) || DEFAULT_MENU_MEDIA_ID;
    }
    return Number(newProjectGroup.menuMediaId) || DEFAULT_MENU_MEDIA_ID;
  };

  const [currentMenuMediaId, setCurrentMenuMediaId] = useState<number>(initialMenuMediaId);

  useEffect(() => {
    dispatch(closeStandardModal("modal-gallery"));
  }, []);

  const {
    media: relevantMedia,
    status: relevantMediaStatus,
    retry: retryRelevantMedia,
  } = useMediaById(currentMenuMediaId > 0 ? currentMenuMediaId : undefined);
  const isMenuMediaLoading =
    currentMenuMediaId > 0 && !relevantMedia && relevantMediaStatus === "loading";
  const isMenuMediaLoadError = !relevantMedia && relevantMediaStatus === "error";

  // Dance around the media preview issues that arose from repurposing the "EditNodeForm" component
  const getEnhancedBgMusicField = () => {
    const enhancedField: {
      type: string;
      mediaType: string;
      fieldName: string;
      currentValue: number;
      relevantSourceMedia: IMedia | undefined;
    } = {
      type: "media",
      mediaType: "image",
      fieldName: "source_app_main_menu_media",
      currentValue: currentMenuMediaId,
      relevantSourceMedia: relevantMedia && {
        ...relevantMedia,
        s3_url: relevantMedia?.thumbnail_s3_url || relevantMedia?.s3_url,
      },
    };
    if (!currentMenuMediaId) {
      enhancedField.currentValue = -1;
      enhancedField.relevantSourceMedia = relevantMedia;
    }
    return enhancedField;
  };

  const openGallery = () => {
    dispatch(
      setMediaSearch({
        mediaType: "image",
        media360tag: "media360",
      }),
    );
    dispatch(setOriginalMediaGallery(currentMenuMediaId));
    dispatch(openStandardModal("modal-gallery"));
  };

  const onSubmit = () => {
    openGallery();
  };

  // Callback from ModalGallery selection
  const onMediaChange = (selectedMedia: IMedia) => {
    dispatch(closeStandardModal("modal-gallery"));

    setCurrentMenuMediaId(Number(selectedMedia.id));
  };

  const handleSave = () => {
    if (editMode) {
      void dispatch(
        updateProjectGroup({
          id: selectedProjectGroup.id,
          payload: {
            source_app_main_menu_media: enhancedField.relevantSourceMedia,
          },
          componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(setAlert({ msg: t("alerts.projectGroupUpdateSuccess"), type: "success" }));
          dispatch(setEditedMenuMedia(enhancedField.relevantSourceMedia as IMedia));
          navigate(`/courses/${selectedProjectGroup.id}/list`);
        });
    }
  };

  const handleCancel = () => {
    navigate(`/courses/${selectedProjectGroup.id}/list`);
  };

  useEffect(() => {
    if (editMode) {
      const tbnId = Number(editedProjectGroup?.source_app_main_menu_media?.id) || 5346;
      setValue("menuMediaId", tbnId);
      setCurrentMenuMediaId(tbnId);
    } else {
      const tbnId = newProjectGroup.menuMediaId;
      setValue("menuMediaId", tbnId);
      setCurrentMenuMediaId(tbnId);
    }
  }, [editedProjectGroup.source_app_main_menu_media, newProjectGroup.menuMediaId, setValue]);

  const enhancedField = getEnhancedBgMusicField();

  return (
    <>
      <h1 className="text-2xl my-5 px-6 font-medium">{t("general.selectMenuMedia")}</h1>
      <ModalGallery
        nodeId={"new-project-group-menu-sphere-app"}
        onMediaChange={onMediaChange}
        allowedMediaTypeOverride="image"
      />

      <form className="flex flex-col grow w-full justify-between" onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-10 w-full my-5 px-6">
          <div
            className="flex flex-col items-center justify-center bg-green-200 p-6 w-full max-w-max"
            data-tour="course-wizard-menu-media"
          >
            <Controller
              control={control}
              name={"source_app_main_menu_media"}
              render={({ field }) => (
                <DynamicMedia
                  field={field}
                  fieldType={enhancedField.type}
                  register={register}
                  handleButtonClick={() => {
                    openGallery();
                  }}
                  currentMedia={enhancedField.currentValue}
                  media={enhancedField.relevantSourceMedia as IMedia}
                  isLoading={isMenuMediaLoading}
                  loadError={isMenuMediaLoadError}
                  onRetryLoad={retryRelevantMedia}
                />
              )}
            />
          </div>
          {(enhancedField?.relevantSourceMedia?.size_in_bytes ?? 0) > MAX_MEDIA_SIZE_IN_BYTE && (
            <p className="text-red-500">{t("pages.createProjectGroup.menuMediaTooBig")}</p>
          )}
        </div>

        {editMode ? (
          <FooterButtons
            cancelText={t("general.cancel")}
            cancelCb={handleCancel}
            nextText={t("general.save")}
            nextCb={handleSave}
          />
        ) : (
          <FooterButtons
            cancelText={t("general.cancel")}
            cancelCb={() =>
              navigate(`/courses${editMode ? `/${selectedProjectGroup.id}/list` : ""}`)
            }
            backText={t("general.back")}
            backCb={decrementStep}
            nextText={t("general.next")}
            nextCb={() => {
              if (editMode) {
                dispatch(setEditedMenuMedia(enhancedField.relevantSourceMedia as IMedia));
              } else {
                dispatch(
                  setNewProjectGroup({ ...newProjectGroup, menuMediaId: currentMenuMediaId }),
                );
              }

              if (incrementStep) {
                incrementStep();
              }
            }}
            nextDisabled={
              !enhancedField.relevantSourceMedia ||
              (enhancedField?.relevantSourceMedia?.size_in_bytes ?? 0) > MAX_MEDIA_SIZE_IN_BYTE
            }
          />
        )}
      </form>
    </>
  );
};

export default CreateProjectGroupMenuMedia;
